import styles from "./styles.module.scss";
import useCreateExpense from "./useCreateExpense.js";

import primaryComponents from "../../primaryComponents/index.js";
import secondaryComponents from "../index.js";
import { useEffect, useState } from "react";
import { RoutedModal } from "../../common/index.js";
import { useValidation } from "./useValidation.js";
import { ExpenseDepartmentForm } from "../Expenses/index.js";
// import globalStyles from "../../../assets/styles/base/globalPage.module.scss";

const CreateExpense = ({
  isEditMode = false,
  isReview = false,
  expenseInfo = {},
  closeWidget = () => {},
}) => {
  const [views, setViews] = useState({
    isCategory: false,
    isDepartment: false,
  });

  const {
    isLoadingDocUpload,
    isCreatingExpense,
    isLoading,
    bankList,
    storeList,
    expenseCategories,
    expenseDetails,
    expenseDepartments,
    chargeVat,
    setExpenseDetails,
    handleSubmitExpense,
    handleUploadMedia,
    handleResolveAccountNumber,
    setChargeVat,
  } = useCreateExpense({ closeWidget, expenseInfo, isEditMode });

  const {
    handleSubmit,
    formState: { errors },
    control,
    trigger,
    register,
    setValue,
  } = useValidation({ ...expenseDetails });

  const paymentMethods = [
    { name: "Transfer", id: "transfer" },
    { name: "Card", id: "card" },
    { name: "Cash", id: "cash" },
  ];

  const recurrenceFrequencies = [
    { name: "Monthly", id: "monthly" },
    { name: "Weekly", id: "weekly" },
  ];

  useEffect(() => {
    setValue("make_refund", expenseDetails?.make_refund);

    if (!expenseDetails?.make_refund && !isReview)
      setExpenseDetails((prev) => ({
        ...prev,
        account_name: null,
        account_number: "",
        bank_code: "",
      }));
  }, [expenseDetails?.make_refund]);

  useEffect(() => {
    setValue("is_recurring", expenseDetails?.is_recurring);

    if (!expenseDetails?.is_recurring)
      setExpenseDetails((prev) => ({
        ...prev,
        recurrence_frequency: "",
        recurrence_start_date: "",
      }));
  }, [expenseDetails?.is_recurring]);

  return (
    <>
      <secondaryComponents.LoaderHelper isLoading={isLoading} classNames="mt-5">
        <form
          id="createExpenseForm"
          onSubmit={handleSubmit(handleSubmitExpense)}
        >
          <div
            className="d-flex flex-column flex-md-row"
            style={{ gap: "10px" }}
          >
            <primaryComponents.InputFieldV2
              control={control}
              classNames="white"
              label="Expense Name"
              placeholder="Enter expense name"
              name="expenseName"
              value={expenseDetails?.name}
              isRequired
              register={register}
              errorMessage={errors.expenseName}
              onKeyUp={() =>
                errors.expenseName !== undefined && trigger("expenseName")
              }
              onChange={({ target: { value } }) => {
                setExpenseDetails((prevState) => ({
                  ...prevState,
                  name: value,
                }));
              }}
            />
            <div className="w-100">
              <primaryComponents.SelectFieldV2
                control={control}
                name="store"
                label="Store"
                isRequired
                options={storeList}
                idKey="id"
                nameKey="name"
                onChange={(value) => {
                  setExpenseDetails((prevState) => ({
                    ...prevState,
                    store: value,
                  }));
                }}
                value={
                  storeList?.find(
                    (store) => `${store?.id}` === `${expenseDetails?.store}`,
                  )?.name || "Select store"
                }
                classNames="store-select"
                isSearchable={false}
                errorMessage={errors.store}
              />
            </div>
          </div>

          <div
            className="d-flex flex-column flex-md-row"
            style={{ gap: "10px" }}
          >
            <div className="w-100">
              <label>
                <span>Were you charged VAT on this Expense?</span>
              </label>
              <div className="d-flex" style={{ gap: "10px" }}>
                <primaryComponents.BoxedRadioInput
                  name="chargeVat"
                  value={true}
                  id="YesChargeVat"
                  isChecked={chargeVat === true}
                  classNames={`whites ${styles.boxedRadio}`}
                  setRadioValue={() => {
                    setChargeVat(true);
                    setExpenseDetails((prevState) => ({
                      ...prevState,
                      vat_rate: expenseDetails?.vat_rate || 7.5,
                    }));
                  }}
                >
                  Yes
                </primaryComponents.BoxedRadioInput>
                <primaryComponents.BoxedRadioInput
                  name="chargeVat"
                  value={false}
                  id="NoChargeVat"
                  isChecked={chargeVat === false}
                  classNames={`whites ${styles.boxedRadio}`}
                  setRadioValue={() => {
                    setChargeVat(false);
                    setExpenseDetails((prevState) => ({
                      ...prevState,
                      vat_rate: 0,
                    }));
                  }}
                >
                  No
                </primaryComponents.BoxedRadioInput>
              </div>
            </div>
            <primaryComponents.AmountFieldV2
              control={control}
              classNames="white"
              label={
                <span>
                  <span>Amount </span>
                  {chargeVat && (
                    <span className="purple--text small-text-size">
                      (Inclusive of VAT)
                    </span>
                  )}
                </span>
              }
              placeholder="Enter amount"
              value={expenseDetails?.amount}
              name="amount"
              currency_code={
                storeList?.find(
                  (storeItem) =>
                    `${storeItem?.id}` === `${expenseDetails.store}`,
                )?.currency_code
              }
              isRequired
              register={register}
              errorMessage={errors.amount}
              onKeyUp={() => errors.amount !== undefined && trigger("amount")}
              onChange={({ target: { value } }) => {
                setExpenseDetails((prevState) => ({
                  ...prevState,
                  amount: value,
                }));
              }}
            />
          </div>

          <div
            className="d-flex flex-column flex-md-row"
            style={{ gap: "10px" }}
          >
            <primaryComponents.VatPercentField
              label="VAT Rate"
              name="vat_rate"
              control={control}
              isDisabled={!chargeVat}
              value={expenseDetails?.vat_rate}
              onChange={({ percentValue }) => {
                setExpenseDetails((prevState) => ({
                  ...prevState,
                  vat_rate: percentValue,
                }));
              }}
              register={register}
              type="number"
              min="0"
              max="100"
              step="0.01"
              errorMessage={errors.vat_rate}
              dividedValue={expenseDetails?.amount || 0}
            />
            <primaryComponents.DateInputV2
              classNames="white"
              label="Expense Date"
              control={control}
              name="expenseDate"
              isRequired
              register={register}
              errorMessage={errors.expenseDate}
              value={expenseDetails?.expense_date}
              onKeyUp={() =>
                errors.expenseDate !== undefined && trigger("expenseDate")
              }
              onChange={({ target: { value } }) => {
                setExpenseDetails((prevState) => ({
                  ...prevState,
                  expense_date: value,
                }));
              }}
            />
          </div>

          <div
            className="d-flex flex-column flex-md-row"
            style={{ gap: "10px" }}
          >
            <div className="w-100">
              <primaryComponents.SelectFieldV2
                label={
                  <span>
                    <span>Category </span>(
                    <span
                      className="d-inline-block purple--text cursor-pointer"
                      onClick={() =>
                        setViews((prev) => ({ ...prev, isCategory: true }))
                      }
                    >
                      Add new category
                    </span>
                    )
                  </span>
                }
                isRequired
                options={expenseCategories}
                nameKey={"name"}
                idKey={"id"}
                value={
                  expenseCategories?.find(
                    (category) =>
                      `${category?.id}` === `${expenseDetails?.category}`,
                  )?.name || "Select Category"
                }
                name="category"
                control={control}
                errorMessage={errors.category}
                onChange={(value, label) => {
                  setExpenseDetails((prevState) => ({
                    ...prevState,
                    category: value,
                    category_label: label,
                  }));
                }}
              />
            </div>

            <div className="w-100">
              <primaryComponents.SelectFieldV2
                label="Payment Method"
                isRequired
                options={paymentMethods}
                nameKey={"name"}
                idKey={"id"}
                name="paymentMethod"
                control={control}
                value={
                  paymentMethods?.find(
                    (paymentMethod) =>
                      `${paymentMethod?.id}` ===
                      `${expenseDetails?.payment_method}`,
                  )?.name || "Select payment method for this expense"
                }
                errorMessage={errors.paymentMethod}
                onChange={(value, label) => {
                  setExpenseDetails((prevState) => ({
                    ...prevState,
                    payment_method: value,
                    payment_method_label: label,
                  }));
                }}
              />
            </div>
          </div>

          <div
            className="d-flex flex-column flex-md-row"
            style={{ gap: "10px" }}
          >
            <div className="w-100">
              <primaryComponents.SelectFieldV2
                control={control}
                label={
                  <span>
                    <span>Department </span>(
                    <span
                      className="d-inline-block purple--text cursor-pointer"
                      onClick={() =>
                        setViews((prev) => ({ ...prev, isDepartment: true }))
                      }
                    >
                      Add new department
                    </span>
                    )
                  </span>
                }
                options={expenseDepartments}
                nameKey={"name"}
                idKey={"id"}
                name="department"
                value={
                  expenseDepartments?.find(
                    (department) =>
                      `${department?.id}` === `${expenseDetails?.department}`,
                  )?.name || "Select department"
                }
                onChange={(value) => {
                  setExpenseDetails((prevState) => ({
                    ...prevState,
                    department: value,
                  }));
                }}
              />
            </div>

            <div className="w-100">
              <label>
                {/* <span className="text-danger">*</span> */}
                <span>Expense Proof</span>
              </label>
              <primaryComponents.FileInput
                name="Drag and drop document here or Browse"
                onChange={(document) => {
                  if (document) handleUploadMedia(document);
                }}
              />
            </div>
          </div>

          <primaryComponents.TextAreaV2
            classNames="white w-100"
            label="Description"
            placeholder="Describe this expense "
            control={control}
            name="description"
            value={expenseDetails?.description}
            register={register}
            errorMessage={errors.description}
            onKeyUp={() =>
              errors.description !== undefined && trigger("description")
            }
            onChange={({ target: { value } }) => {
              setExpenseDetails((prevState) => ({
                ...prevState,
                description: value,
              }));
            }}
          />

          {!isEditMode && (
            <div className="mb-3">
              <primaryComponents.Checkbox
                isChecked={expenseDetails?.is_recurring}
                id="isRecurring"
                name="isRecurring"
                onChange={(checked) => {
                  setExpenseDetails((prevState) => ({
                    ...prevState,
                    is_recurring: checked,
                  }));
                }}
              >
                <span className="mt-1 d-block">
                  Is this a recurring expense?
                </span>
              </primaryComponents.Checkbox>

              {expenseDetails?.is_recurring && (
                <div
                  className="d-flex flex-column flex-md-row mt-3"
                  style={{ gap: "10px" }}
                >
                  <div className="w-100">
                    <primaryComponents.SelectFieldV2
                      control={control}
                      name="recurrenceFrequency"
                      label="Recurrence Frequency"
                      isRequired
                      options={recurrenceFrequencies}
                      idKey="id"
                      nameKey="name"
                      value={
                        recurrenceFrequencies.find(
                          (frequency) =>
                            frequency?.id ===
                            expenseDetails?.recurrence_frequency,
                        )?.name || "Select frequency"
                      }
                      onChange={(value) => {
                        setExpenseDetails((prevState) => ({
                          ...prevState,
                          recurrence_frequency: value,
                        }));
                      }}
                      isSearchable={false}
                      errorMessage={errors.recurrenceFrequency}
                    />
                  </div>
                  <primaryComponents.DateInputV2
                    classNames="white"
                    label="Recurrence Start Date"
                    control={control}
                    name="recurrenceStartDate"
                    isRequired
                    register={register}
                    errorMessage={errors.recurrenceStartDate}
                    value={expenseDetails?.recurrence_start_date}
                    onKeyUp={() =>
                      errors.recurrenceStartDate !== undefined &&
                      trigger("recurrenceStartDate")
                    }
                    onChange={({ target: { value } }) => {
                      setExpenseDetails((prevState) => ({
                        ...prevState,
                        recurrence_start_date: value,
                      }));
                    }}
                  />
                </div>
              )}
            </div>
          )}

          <secondaryComponents.CountryItemPermission>
            <div className="mb-3">
              <label>
                <span className="text-danger">*</span>
                <span>Re-imburse or Disburse Funds For This Expense?</span>
              </label>
              <div className="d-flex" style={{ gap: "10px" }}>
                <primaryComponents.BoxedRadioInput
                  name="reImburse"
                  value={true}
                  id="YesReImburse"
                  isChecked={expenseDetails.make_refund}
                  classNames="whites"
                  setRadioValue={() => {
                    setExpenseDetails((prevState) => ({
                      ...prevState,
                      make_refund: true,
                    }));
                  }}
                >
                  Yes
                </primaryComponents.BoxedRadioInput>
                <primaryComponents.BoxedRadioInput
                  name="reImburse"
                  value={false}
                  id="NoReImburse"
                  isChecked={!expenseDetails.make_refund}
                  classNames="whites"
                  setRadioValue={() => {
                    setExpenseDetails((prevState) => ({
                      ...prevState,
                      make_refund: false,
                    }));
                  }}
                >
                  No
                </primaryComponents.BoxedRadioInput>
              </div>
            </div>
          </secondaryComponents.CountryItemPermission>

          {expenseDetails?.make_refund && !isReview ? (
            <>
              <primaryComponents.SelectFieldV2
                control={control}
                name="bankCode"
                label="Bank"
                isRequired
                value={
                  expenseDetails?.bank_code
                    ? expenseDetails?.bank_code_label
                    : "Select Bank"
                }
                options={bankList}
                nameKey="bank_name"
                idKey="bank_code"
                onChange={(value, label) => {
                  setExpenseDetails((prevState) => ({
                    ...prevState,
                    bank_code: value,
                    bank_code_label: label,
                  }));
                  if (expenseDetails?.account_number.length === 10)
                    handleResolveAccountNumber(
                      value,
                      expenseDetails?.account_number,
                    );
                }}
                errorMessage={errors.bankCode}
              />

              <div>
                <primaryComponents.InputFieldV2
                  control={control}
                  label="Account Number"
                  isRequired
                  placeholder="Enter 10-digit account number"
                  name="accountNumber"
                  classNames="white"
                  floatError={false}
                  value={expenseDetails?.account_number}
                  onChange={({ target: { value } }) => {
                    setExpenseDetails((prevState) => ({
                      ...prevState,
                      account_number: value,
                    }));
                    if (value.length === 10)
                      handleResolveAccountNumber(
                        expenseDetails?.bank_code,
                        value,
                      );
                  }}
                  onKeyUp={() =>
                    errors.accountNumber !== undefined &&
                    trigger("accountNumber")
                  }
                  register={register}
                  errorMessage={errors.accountNumber}
                />
                {expenseDetails?.account_name && (
                  <p className="small-text-size purple--text">
                    {expenseDetails?.account_name}
                  </p>
                )}
              </div>
            </>
          ) : null}
        </form>

        {/* <div className={globalStyles.stickyBottomCover}> */}
        <primaryComponents.Button
          classNames="btn btn--primary cover mt-auto"
          type="submit"
          form="createExpenseForm"
          isLoading={isCreatingExpense || isLoadingDocUpload}
          // loadingText={isLoadingDocUpload ? "Uploading..." : "Submitting..."}
          isDisabled={
            isCreatingExpense ||
            isLoadingDocUpload ||
            (expenseDetails?.make_refund && !expenseDetails?.account_name)
          }
        >
          {isEditMode ? "Update" : "Submit"}
        </primaryComponents.Button>
        {/* </div> */}
      </secondaryComponents.LoaderHelper>

      <RoutedModal
        width={550}
        isActive={views?.isCategory}
        closeModal={() => setViews((prev) => ({ ...prev, isCategory: false }))}
        headerTitle="Create Expense"
        style={{ height: "auto" }}
      >
        <secondaryComponents.CreateExpenseCategory
          closeWidget={() =>
            setViews((prev) => ({ ...prev, isCategory: false }))
          }
        />
      </RoutedModal>

      <RoutedModal
        isActive={views?.isDepartment}
        width="550px"
        headerTitle="Create Department"
        closeModal={() =>
          setViews((prev) => ({ ...prev, isDepartment: false }))
        }
        style={{ height: "auto" }}
      >
        <ExpenseDepartmentForm
          closeModal={() =>
            setViews((prev) => ({ ...prev, isDepartment: false }))
          }
        />
      </RoutedModal>
    </>
  );
};
export default CreateExpense;
